import { useState, useMemo, useCallback } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  LayoutDashboardIcon,
  SettingsIcon,
  BuildingIcon,
  GaugeIcon,
  FileTextIcon,
  ShieldIcon,
  ServerIcon,
  FlagIcon,
  ClipboardListIcon,
  HomeIcon,
  MapPinIcon,
  ShieldCheckIcon,
  PlugIcon,
  ActivityIcon,
  MenuIcon,
  ShieldAlertIcon,
  TrendingUpIcon,
  FileStackIcon,
  SendIcon,
  UsersIcon,
  SearchIcon,
  MapIcon,
  DollarSignIcon,
  BarChart3Icon,
  ShieldHalfIcon,
  NetworkIcon,
  CoinsIcon,
  HandshakeIcon,
  WalletIcon,
  InboxIcon,
  LayersIcon,
  KeyIcon,
  KeyRoundIcon,
  LifeBuoyIcon,
  DatabaseIcon,
  WorkflowIcon,
} from 'lucide-react';
import {
  AppHeader,
  PortalFrame,
  BuildVersion,
  CommandPalette,
  CommandPaletteTrigger,
  IdleSignOut,
  ThemeToggle,
  UserProfileMenu,
  NotificationPanel,
  RoleBasedNav,
  NavBadge,
} from '@aaimplatform/ui';
import type {
  PaletteResult,
  UserProfile,
  UserMenuItem,
  BreadcrumbItem,
  NavGroupDefinition,
} from '@aaimplatform/ui';
import { useAuthStore } from '../stores/auth';
import { IDLE_SIGN_OUT_CHANNEL } from '../stores/idleSignOut';
import { useNotifications } from '../hooks/useNotifications';
import { useTaskCount } from '../hooks/useTasks';
import { PLATFORM_ADMIN_EQUIVALENT_ROLES } from '../utils/role-helpers';
import { resolveActiveNavItemId } from './active-nav';
import { resolvePageInfo } from './page-info-resolve';
import { SEARCH_CATEGORIES, searchPaletteEntries, paletteEntryHref } from './palette-search';

// ---------------------------------------------------------------------------
// Constants: Sidebar Width
// ---------------------------------------------------------------------------

// Sidebar width now lives with the frame that applies it: PORTAL_SIDEBAR_WIDTH
// in @aaimplatform/ui (packages/ui/src/shell/PortalFrame.tsx). Same 260px.

// ---------------------------------------------------------------------------
// Page Info for Breadcrumbs
// ---------------------------------------------------------------------------

function buildBreadcrumbs(labels: string[], paths?: (string | undefined)[]): BreadcrumbItem[] {
  return labels.map((label, index) => {
    const isLast = index === labels.length - 1;
    return {
      label,
      href: isLast ? undefined : paths?.[index],
    };
  });
}

const PAGE_INFO: Record<string, { title: string; breadcrumbs: BreadcrumbItem[] }> = {
  // ---------------------------------------------------------------------------
  // Added 2026-08-14. A router path absent from this map falls through to the
  // generic `{ title: 'Page' }` at the bottom of currentPageInfo -- a plausible
  // word rather than a loud one, which is why 20 routes shipped with it and
  // nobody noticed. Found by diffing App.tsx's <Route path=...> set against
  // these keys after the workflow-recipes page rendered its header as 'Page'.
  // /login and /auth/callback stay absent deliberately: they render outside
  // this layout.
  // ---------------------------------------------------------------------------
  '/institutions': {
    title: 'Institutions',
    breadcrumbs: buildBreadcrumbs(['Platform', 'Institutions'], ['/']),
  },
  // /legal/signatures only redirects to /ucc/filings (App.tsx), and
  // page-info-route-parity.test.ts requires an entry for every router path.
  '/legal/signatures': {
    title: 'Signature Ledger',
    breadcrumbs: buildBreadcrumbs(['Platform', 'Legal', 'Signatures'], ['/']),
  },
  '/support': {
    title: 'Support Cases',
    breadcrumbs: buildBreadcrumbs(['Platform', 'Support'], ['/']),
  },
  '/support/agents': {
    title: 'Support Agents',
    breadcrumbs: buildBreadcrumbs(['Platform', 'Support', 'Agents'], ['/']),
  },
  '/support/sla': {
    title: 'Support SLA Rules',
    breadcrumbs: buildBreadcrumbs(['Platform', 'Support', 'SLA'], ['/']),
  },
  '/support/recovery-cases': {
    title: 'Recovery Cases',
    breadcrumbs: buildBreadcrumbs(['Platform', 'Support', 'Recovery Cases'], ['/']),
  },
  '/platform-config': {
    title: 'Platform Configuration',
    breadcrumbs: buildBreadcrumbs(['Platform', 'Configuration'], ['/']),
  },
  '/platform-config/general': {
    title: 'General Settings',
    breadcrumbs: buildBreadcrumbs(['Platform', 'Configuration', 'General'], ['/']),
  },
  '/platform-config/security': {
    title: 'Security Settings',
    breadcrumbs: buildBreadcrumbs(['Platform', 'Configuration', 'Security'], ['/']),
  },
  '/platform-config/api': {
    title: 'API Settings',
    breadcrumbs: buildBreadcrumbs(['Platform', 'Configuration', 'API'], ['/']),
  },
  '/platform-config/rate-limits': {
    title: 'Provider Rate Limits',
    breadcrumbs: buildBreadcrumbs(['Platform', 'Configuration', 'Rate Limits'], ['/']),
  },
  '/platform-config/notifications': {
    title: 'Notification Defaults',
    breadcrumbs: buildBreadcrumbs(['Platform', 'Configuration', 'Notifications'], ['/']),
  },
  '/platform-config/integrations': {
    title: 'Integration Credentials',
    breadcrumbs: buildBreadcrumbs(['Platform', 'Configuration', 'Integrations'], ['/']),
  },
  '/platform-config/feature-flags': {
    title: 'System Feature Flags',
    breadcrumbs: buildBreadcrumbs(['Platform', 'Configuration', 'Feature Flags'], ['/']),
  },
  '/platform-config/data': {
    title: 'Data and Storage',
    breadcrumbs: buildBreadcrumbs(['Platform', 'Configuration', 'Data'], ['/']),
  },
  '/platform-config/defaults': {
    title: 'System Defaults',
    breadcrumbs: buildBreadcrumbs(['Platform', 'Configuration', 'Defaults'], ['/']),
  },
  '/platform-config/lending': {
    title: 'Lending Defaults',
    breadcrumbs: buildBreadcrumbs(['Platform', 'Configuration', 'Lending'], ['/']),
  },
  '/platform-config/collateral-baselines': {
    title: 'Collateral Baselines',
    breadcrumbs: buildBreadcrumbs(['Platform', 'Configuration', 'Collateral Baselines'], ['/']),
  },
  '/platform-config/workflow-recipes': {
    title: 'Workflow Recipes',
    breadcrumbs: buildBreadcrumbs(['Platform', 'Configuration', 'Workflow Recipes'], ['/']),
  },
  '/': {
    title: 'Dashboard',
    breadcrumbs: buildBreadcrumbs(['Platform Dashboard']),
  },
  '/tenants': {
    title: 'Tenants',
    breadcrumbs: buildBreadcrumbs(['Platform', 'Tenants'], ['/']),
  },
  '/data-browser': {
    title: 'Data Browser',
    breadcrumbs: buildBreadcrumbs(['Platform', 'Developer', 'Data Browser'], ['/']),
  },
  '/pledges': {
    title: 'Pledges (All Tenants)',
    breadcrumbs: buildBreadcrumbs(['Platform', 'Pledge Operations', 'Pledges'], ['/']),
  },
  '/tasks': {
    title: 'FI Tasks',
    breadcrumbs: buildBreadcrumbs(['Platform', 'Pledge Operations', 'Tasks'], ['/', '/pledges']),
  },
  '/notification-routing': {
    title: 'Pledge Notifications',
    breadcrumbs: buildBreadcrumbs(['Platform', 'System', 'Pledge Notifications'], ['/']),
  },
  '/integrations': {
    title: 'Integrations',
    breadcrumbs: buildBreadcrumbs(['Platform', 'Integrations'], ['/']),
  },
  '/aaim-services': {
    title: 'Aaim Services',
    breadcrumbs: buildBreadcrumbs(['Platform', 'System', 'Aaim Services'], ['/']),
  },
  '/integrations/attom': {
    title: 'ATTOM Property Data',
    breadcrumbs: buildBreadcrumbs(['Platform', 'Integrations', 'ATTOM Property Data'], ['/', '/integrations']),
  },
  '/ucc': {
    title: 'UCC Filing Dashboard',
    breadcrumbs: buildBreadcrumbs(['UCC Filing', 'Dashboard']),
  },
  '/ucc/filings': {
    title: 'UCC Filings',
    breadcrumbs: buildBreadcrumbs(['UCC Filing', 'Filings'], ['/ucc']),
  },
  '/ucc/submissions': {
    title: 'UCC Submissions',
    breadcrumbs: buildBreadcrumbs(['UCC Filing', 'Submissions'], ['/ucc']),
  },
  '/ucc/entities': {
    title: 'UCC Entities',
    breadcrumbs: buildBreadcrumbs(['UCC Filing', 'Entities'], ['/ucc']),
  },
  '/ucc/search': {
    title: 'UCC Search',
    breadcrumbs: buildBreadcrumbs(['UCC Filing', 'Search'], ['/ucc']),
  },
  '/ucc/jurisdictions': {
    title: 'UCC Jurisdictions',
    breadcrumbs: buildBreadcrumbs(['UCC Filing', 'Jurisdictions'], ['/ucc']),
  },
  '/ucc/fees': {
    title: 'UCC Fees',
    breadcrumbs: buildBreadcrumbs(['UCC Filing', 'Fees'], ['/ucc']),
  },
  '/ucc/analytics': {
    title: 'UCC Analytics',
    breadcrumbs: buildBreadcrumbs(['UCC Filing', 'Analytics'], ['/ucc']),
  },
  '/ucc/compliance': {
    title: 'UCC Audit Trail',
    breadcrumbs: buildBreadcrumbs(['UCC Filing', 'Audit Trail'], ['/ucc']),
  },
  '/syndication': {
    title: 'Liquidity Network Dashboard',
    breadcrumbs: buildBreadcrumbs(['Syndication', 'Network Dashboard']),
  },
  '/syndication/offerings': {
    title: 'Participation Offerings',
    breadcrumbs: buildBreadcrumbs(['Syndication', 'Offerings'], ['/syndication']),
  },
  '/syndication/positions': {
    title: 'Participation Positions',
    breadcrumbs: buildBreadcrumbs(['Syndication', 'Positions'], ['/syndication']),
  },
  '/syndication/crt': {
    title: 'CRT Tokens',
    breadcrumbs: buildBreadcrumbs(['Syndication', 'CRT Tokens'], ['/syndication']),
  },
  '/collateral/valuations': {
    title: 'Property Valuations',
    breadcrumbs: buildBreadcrumbs(['Platform', 'Collateral', 'Property Valuations'], ['/']),
  },
  '/collateral/title-searches': {
    title: 'Title Searches',
    breadcrumbs: buildBreadcrumbs(['Platform', 'Collateral', 'Title Searches'], ['/']),
  },
  '/collateral/lien-verifications': {
    title: 'Lien Verifications',
    breadcrumbs: buildBreadcrumbs(['Platform', 'Collateral', 'Lien Verifications'], ['/']),
  },
  '/feature-flags': {
    title: 'Feature Flags',
    breadcrumbs: buildBreadcrumbs(['Platform', 'System', 'Feature Flags'], ['/']),
  },
  '/audit-logs': {
    title: 'Audit Logs',
    breadcrumbs: buildBreadcrumbs(['Platform', 'System', 'Audit Logs'], ['/']),
  },
  '/system-health': {
    title: 'System Health',
    breadcrumbs: buildBreadcrumbs(['Platform', 'System', 'System Health'], ['/']),
  },
  '/settings': {
    // App.tsx redirects /settings to /platform-config/general, the one
    // General Settings route; this entry covers the redirect frame.
    title: 'General Settings',
    breadcrumbs: buildBreadcrumbs(['Platform', 'System', 'Settings'], ['/']),
  },
  '/preferences': {
    title: 'User Preferences',
    breadcrumbs: buildBreadcrumbs(['Platform', 'Preferences'], ['/']),
  },
  '/identity-providers': {
    title: 'Identity Providers',
    breadcrumbs: buildBreadcrumbs(['Identity & Auth', 'Identity Providers']),
  },
  '/roles': {
    title: 'Roles & Permissions',
    breadcrumbs: buildBreadcrumbs(['Identity & Auth', 'Roles']),
  },
  '/regulatory': {
    title: 'Regulatory Readiness',
    breadcrumbs: buildBreadcrumbs(['Compliance', 'Regulatory Readiness']),
  },
  '/regulatory/:jurisdictionCode': {
    title: 'Jurisdiction Detail',
    breadcrumbs: buildBreadcrumbs(['Compliance', 'Regulatory Readiness', 'Detail'], [undefined, '/regulatory']),
  },
  '/asset-classes': {
    title: 'Asset Classes',
    breadcrumbs: buildBreadcrumbs(['Asset Taxonomy', 'Asset Classes']),
  },
  '/syndication/program-settings': {
    title: 'CRT Program Settings',
    breadcrumbs: buildBreadcrumbs(['Liquidity Network', 'Program Settings'], ['/syndication']),
  },
  '/syndication/subscriptions-queue': {
    title: 'CRT Subscriptions Queue',
    breadcrumbs: buildBreadcrumbs(['Liquidity Network', 'Subscriptions Queue'], ['/syndication']),
  },
  '/strategy-tools': {
    title: 'Economic Modeling',
    breadcrumbs: buildBreadcrumbs(['Platform', 'Economic Modeling'], ['/']),
  },
};

// ---------------------------------------------------------------------------
// Navigation Group Definitions (RoleBasedNav)
// ---------------------------------------------------------------------------

const NAV_ID_TO_PATH: Record<string, string> = {
  'dashboard': '/',
  'tenants': '/tenants',
  'data-browser': '/data-browser',
  'integrations': '/integrations',
  'aaim-services': '/aaim-services',
  // 'legal-signatures' nav id removed 2026-04-29 (Sprint C11 Lane 5):
  // page deleted; signature flow consolidated into UCC filings.
  'ucc-dashboard': '/ucc',
  'ucc-filings': '/ucc/filings',
  'ucc-submissions': '/ucc/submissions',
  'ucc-entities': '/ucc/entities',
  'ucc-search': '/ucc/search',
  'ucc-jurisdictions': '/ucc/jurisdictions',
  'ucc-fees': '/ucc/fees',
  'ucc-analytics': '/ucc/analytics',
  'ucc-compliance': '/ucc/compliance',
  'collateral-valuations': '/collateral/valuations',
  'collateral-title-searches': '/collateral/title-searches',
  'collateral-lien-verifications': '/collateral/lien-verifications',
  'feature-flags': '/feature-flags',
  'audit-logs': '/audit-logs',
  'system-health': '/system-health',
  'platform-identity': '/platform-config/general',
  'identity-providers': '/identity-providers',
  'roles': '/roles',
  'regulatory-readiness': '/regulatory',
  'economic-modeling': '/strategy-tools',
  'pledges': '/pledges',
  'support': '/support',
  'support-sla': '/support/sla',
  'support-agents': '/support/agents',
  'support-recovery-cases': '/support/recovery-cases',
  'syndication-dashboard': '/syndication',
  'syndication-offerings': '/syndication/offerings',
  'syndication-positions': '/syndication/positions',
  'syndication-crt': '/syndication/crt',
  'tasks': '/tasks',
  'notification-routing': '/notification-routing',
  'asset-classes': '/asset-classes',
  'syndication-program-settings': '/syndication/program-settings',
  'syndication-subscriptions-queue': '/syndication/subscriptions-queue',
  // F-PORTAL-AUDIENCE-SPLIT (2026-05-17)
  'platform-config': '/platform-config',
  'platform-config-security': '/platform-config/security',
  'platform-config-notifications': '/platform-config/notifications',
  'platform-config-api': '/platform-config/api',
  'platform-config-data': '/platform-config/data',
  'platform-config-integrations': '/platform-config/integrations',
  'platform-config-defaults': '/platform-config/defaults',
  'platform-config-rate-limits': '/platform-config/rate-limits',
  'platform-config-lending': '/platform-config/lending',
  'platform-config-collateral-baselines': '/platform-config/collateral-baselines',
  'platform-config-feature-flags': '/platform-config/feature-flags',
  'platform-config-workflow-recipes': '/platform-config/workflow-recipes',
};

/** The nav marker on an item whose backend has no deployed revision yet. */
const UCC_NOT_DEPLOYED = 'Not deployed';

const NAV_GROUPS: NavGroupDefinition[] = [
  // nav-id: platformGroupNav
  {
    id: 'platform',
    label: 'Platform',
    items: [
      { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboardIcon },
      { id: 'tenants', label: 'Tenants', icon: BuildingIcon },
      // Sprint E (2026-05-17): All Institutions retired. The legacy
      // alternative-roster lens duplicated the Tenants surface; type
      // segmentation lives in the Tenants Type column + filter now.
    ],
  },
  // nav-id: pledgeOperationsNav
  {
    id: 'pledge-operations-section',
    label: 'Pledge Operations',
    items: [
      { id: 'pledges', label: 'Pledges', icon: FileTextIcon },
      { id: 'tasks', label: 'FI Tasks', icon: InboxIcon },
    ],
  },
  // nav-id: platformSupportNav -- Aaim-operator support console (node 5,
  // 2026-06-26). Operator-run case queue across all tenants; tickets homed per
  // FI tenant. Re-annotated from `supportNav` 2026-09-15: that id belongs to the
  // consumerPortal member-facing case view, and platform-nav-scope.yaml is keyed
  // on nav_id globally, so the two surfaces collapsed into one manifest row and
  // this operator console inherited personas [customer].
  {
    id: 'support-section',
    label: 'Support',
    items: [
      { id: 'support', label: 'Support Cases', icon: LifeBuoyIcon },
      { id: 'support-sla', label: 'SLA Rules', icon: GaugeIcon },
      { id: 'support-agents', label: 'Agents', icon: UsersIcon },
      // GH #4409: break-glass recovery cases; the page needs recovery:read.
      { id: 'support-recovery-cases', label: 'Recovery Cases', icon: KeyRoundIcon },
    ],
  },
  // nav-id: developerNav -- GH #1824: the sanctioned AI/dev debug surface
  // (operator-scoped, IP-allowlisted, audited read-only data browser + ephemeral
  // paste-able token). AAIM_SUPER_ADMIN only (PLATFORM_ADMIN_EQUIVALENT_ROLES).
  {
    id: 'developer-section',
    label: 'Developer',
    items: [
      { id: 'data-browser', label: 'Data Browser', icon: DatabaseIcon, requiredRole: PLATFORM_ADMIN_EQUIVALENT_ROLES },
    ],
  },
  {
    // Sprint C17 (2026-05-04): Data Providers renamed to Integrations and
    // promoted to its own navgroup as the master third-party integration
    // registry. The previous "Integration Registry" sub-item was a synonym for
    // the same page; collapsed into a single Integrations entry.
    // 2026-07-10: labels corrected from "Partner Registry" to "Integrations"
    // (operator terminology of record; matches the page title, command
    // palette, and Platform Configuration overview card).
    // nav-id: integrationsNav
    id: 'integrations-section',
    label: 'Integrations',
    items: [
      { id: 'integrations', label: 'Integrations', icon: NetworkIcon },
    ],
  },
  // nav-id: collateralDataNav
  {
    id: 'collateral-data-section',
    label: 'Collateral & Data',
    items: [
      { id: 'collateral-valuations', label: 'Property Valuations', icon: HomeIcon },
      { id: 'collateral-title-searches', label: 'Title Searches', icon: MapPinIcon },
      { id: 'collateral-lien-verifications', label: 'Lien Verifications', icon: ShieldCheckIcon },
    ],
  },
  {
    // Sprint Lane B1 (2026-05-17, sprint-portal-uxia-audit): "CRT Operations"
    // group collapsed into "Liquidity Network". Program Settings and
    // Subscriptions Queue paths migrated atomically from /crt/* to
    // /syndication/* per audit Section 3.2 + Section 5.1.
    //
    // Role-gating discipline (Sprint C-CRT-01 + 2026-05-16 role-equivalence):
    // CRT program operations (custodian wire instructions CRUD + cross-tenant
    // subscriptions queue) are platform-admin scope. Three role names share
    // that scope by design per
    // staging/aaim-core-new/services/authorization/src/seed-rbac.ts:
    //   - aaim_super_admin   (canonical SSO Platform Admin, e.g. rob@aaim.com)
    //   - platform_admin     (legacy synonym still emitted by some federated
    //                         identity flows)
    //   - e2e_test_admin     (non-SSO QA fixture, e2etesting@aaim.com; same
    //                         permission scope, distinct name preserves the
    //                         audit trail)
    // RoleBasedNav.hasAccess accepts the array form as ANY-of with both-side
    // case normalization per .claude/rules/authz-case-insensitive.md. The
    // two CRT-program items keep that requiredRole array; the rest of the
    // group is open to all platform users.
    // nav-id: liquidityNetworkNav
    id: 'syndication-section',
    label: 'Liquidity Network',
    items: [
      { id: 'syndication-dashboard', label: 'Network Dashboard', icon: NetworkIcon },
      { id: 'syndication-offerings', label: 'Offerings', icon: HandshakeIcon },
      { id: 'syndication-positions', label: 'Positions', icon: WalletIcon },
      { id: 'syndication-crt', label: 'CRT Tokens', icon: CoinsIcon },
      { id: 'syndication-program-settings', label: 'Program Settings', icon: CoinsIcon, requiredRole: PLATFORM_ADMIN_EQUIVALENT_ROLES },
      { id: 'syndication-subscriptions-queue', label: 'Subscriptions Queue', icon: InboxIcon, requiredRole: PLATFORM_ADMIN_EQUIVALENT_ROLES },
    ],
  },
  // Legal nav section removed 2026-04-29 (Sprint C11 Lane 5):
  // The Electronic Signatures page was deleted; UCC filings own that flow.
  // nav-id: uccFilingNav
  {
    id: 'ucc-filing-section',
    label: 'UCC Filing',
    items: [
      { id: 'ucc-dashboard', label: 'Dashboard', icon: LayoutDashboardIcon },
      { id: 'ucc-filings', label: 'Filings', icon: FileStackIcon },
      // Submissions and Search call filing-submission and ucc-search, which
      // have no Cloud Run revision and no load-balancer rule yet
      // (gcp-infrastructure.yaml status: planned; GH #1734). Both pages show
      // a 404 notice; the marker says so before the click.
      { id: 'ucc-submissions', label: 'Submissions', icon: SendIcon, badge: UCC_NOT_DEPLOYED },
      { id: 'ucc-entities', label: 'Entities', icon: UsersIcon },
      { id: 'ucc-search', label: 'Search', icon: SearchIcon, badge: UCC_NOT_DEPLOYED },
      { id: 'ucc-jurisdictions', label: 'Jurisdictions', icon: MapIcon },
      { id: 'ucc-fees', label: 'Fees', icon: DollarSignIcon },
      { id: 'ucc-analytics', label: 'Analytics', icon: BarChart3Icon },
      { id: 'ucc-compliance', label: 'Audit Trail', icon: ShieldHalfIcon },
    ],
  },
  // nav-id: complianceNav
  {
    id: 'compliance-section',
    label: 'Compliance',
    items: [
      { id: 'regulatory-readiness', label: 'Regulatory Readiness', icon: ShieldAlertIcon },
    ],
  },
  // nav-id: strategyToolsNav
  {
    id: 'strategy-section',
    label: 'Strategy Tools',
    items: [
      { id: 'economic-modeling', label: 'Economic Modeling', icon: TrendingUpIcon },
    ],
  },
  // nav-id: assetTaxonomyNav
  {
    id: 'asset-taxonomy-section',
    label: 'Asset Taxonomy',
    items: [
      { id: 'asset-classes', label: 'Asset Classes', icon: LayersIcon },
    ],
  },
  {
    // F-PORTAL-AUDIENCE-SPLIT (2026-05-17): Platform Configuration sub-pages
    // relocated from admin-portal. The legacy /admin/platform/* paths in
    // admin-portal had breadcrumbs literally labeled "Platform > Platform
    // Configuration > ..." -- the surfaces are platform-operator scope and
    // belong in platform-admin. Sprint C decomposes the prior 11-item flat
    // group into three top-level groups (Platform Policy / Tenant Defaults /
    // Integration Catalog) to respect Miller's Law on the sidebar surface.
    // nav-id: platformConfigurationNav
    id: 'platform-config-overview-section',
    label: 'Platform Configuration',
    items: [
      { id: 'platform-config', label: 'Overview', icon: SettingsIcon },
    ],
  },
  // nav-id: platformPolicyNav
  {
    id: 'platform-policy-section',
    label: 'Platform Policy',
    items: [
      { id: 'platform-config-security', label: 'Security', icon: ShieldIcon },
      { id: 'platform-config-api', label: 'API', icon: PlugIcon },
      { id: 'platform-config-data', label: 'Data and Storage', icon: ServerIcon },
      { id: 'platform-config-feature-flags', label: 'System Feature Flags', icon: FlagIcon },
    ],
  },
  // nav-id: tenantDefaultsNav
  {
    id: 'tenant-defaults-section',
    label: 'Tenant Defaults',
    items: [
      { id: 'platform-config-defaults', label: 'System Defaults', icon: SettingsIcon },
      { id: 'platform-config-lending', label: 'Lending Defaults', icon: WalletIcon },
      { id: 'platform-config-notifications', label: 'Notification Defaults', icon: SettingsIcon },
      { id: 'platform-config-collateral-baselines', label: 'Collateral Baselines', icon: ShieldHalfIcon },
      // The Platform Configuration overview tile opened this page, and no
      // sidebar item did. It sits with the tenant defaults: the recipes an
      // operator publishes here are what an FI admin selects from.
      { id: 'platform-config-workflow-recipes', label: 'Workflow Recipes', icon: WorkflowIcon },
    ],
  },
  // nav-id: integrationCatalogNav
  {
    id: 'integration-catalog-section',
    label: 'Integration Catalog',
    items: [
      { id: 'platform-config-integrations', label: 'Integration Credentials', icon: KeyIcon },
      { id: 'platform-config-rate-limits', label: 'Provider Rate Limits', icon: ActivityIcon },
    ],
  },
  // nav-id: identityAuthNav
  {
    id: 'identity-auth-section',
    label: 'Identity & Auth',
    items: [
      { id: 'identity-providers', label: 'Identity Providers', icon: ShieldIcon },
      { id: 'roles', label: 'Roles & Permissions', icon: ShieldCheckIcon },
    ],
  },
  // nav-id: systemNav
  {
    id: 'system-section',
    label: 'System',
    items: [
      // Sprint C17 (2026-05-04): Aaim internal services moved here from the
      // mixed Integrations list -- they are platform infrastructure, not
      // third-party integrations.
      { id: 'aaim-services', label: 'Aaim Services', icon: ServerIcon },
      { id: 'feature-flags', label: 'Tenant Entitlements', icon: KeyIcon },
      { id: 'audit-logs', label: 'Audit Logs', icon: ClipboardListIcon },
      { id: 'system-health', label: 'System Health', icon: ActivityIcon },
      { id: 'platform-identity', label: 'General Settings', icon: SettingsIcon },
      { id: 'notification-routing', label: 'Pledge Notifications', icon: InboxIcon },
    ],
  },
];

// ---------------------------------------------------------------------------
// PlatformAdminLayout Component
// ---------------------------------------------------------------------------

/**
 * PlatformAdminLayout - Modernized platform admin layout using the UI Kit Phase 0 components.
 *
 * Architecture:
 * - AppHeader: Top navigation with breadcrumbs and action toolbar
 * - TreeNav: Sidebar navigation with section-based grouping
 * - CommandPalette: Cmd+K command palette
 * - Breadcrumbs: Inline in AppHeader via breadcrumbs prop
 *
 * This layout replaces the legacy MainLayout while preserving all existing
 * routing and page functionality.
 */
export function PlatformAdminLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const queryClient = useQueryClient();

  // -------------------------------------------------------------------------
  // Breadcrumbs - derive from current route
  // -------------------------------------------------------------------------

  // Exact key, then a parameterized key ('/regulatory/:jurisdictionCode')
  // through matchPath, then the longest literal prefix (/tenants/:id reads
  // the /tenants entry). See page-info-resolve.ts.
  const currentPageInfo = useMemo(
    (): { title: string; breadcrumbs: BreadcrumbItem[] } =>
      resolvePageInfo(location.pathname, PAGE_INFO) ?? {
        title: 'Page',
        breadcrumbs: buildBreadcrumbs(['Platform', 'Page'], ['/']),
      },
    [location.pathname],
  );

  // -------------------------------------------------------------------------
  // User profile for top nav
  // -------------------------------------------------------------------------

  // F-XPORTAL-AVATAR-001 (2026-05-16): omit `initials` so the canonical
  // two-character extraction in @aaimplatform/ui UserProfileMenu
  // (getInitials) is used. Passing a single-char override defeated the
  // package logic and rendered "R" instead of "RG" for "Robert Goodyear".
  const userProfile: UserProfile = useMemo(() => ({
    name: user?.name || 'Admin',
    email: user?.email || '',
    role: user?.roles?.[0] || 'Platform Admin',
  }), [user?.name, user?.email, user?.roles]);

  const userMenuItems: UserMenuItem[] = useMemo(() => [
    { id: 'preferences', label: 'Preferences', onClick: () => navigate('/preferences') },
  ], [navigate]);

  // Sign-out, by hand or after 30 minutes without input (DCF-62), also drops
  // the query cache, as the admin portal does, so no cached rows outlive the
  // session in this page.
  const handleSignOut = useCallback(() => {
    logout();
    queryClient.clear();
    navigate('/login');
  }, [logout, queryClient, navigate]);

  // -------------------------------------------------------------------------
  // Notifications
  // -------------------------------------------------------------------------

  const { data: notifications = [] } = useNotifications();

  // -------------------------------------------------------------------------
  // FI Task Queue badge - live pending/overSla counts
  // -------------------------------------------------------------------------

  const { data: taskCountData, isError: isTaskCountError } = useTaskCount();

  // The badge renders only from a count the server returned. While the count
  // is unread, or when its read failed, the item carries no badge; it is
  // never fed a substituted 0.
  const enrichedNavGroups: NavGroupDefinition[] = useMemo(() => {
    const pendingCount = taskCountData?.pendingCount;
    const overSlaCount = taskCountData?.overSlaCount;
    if (isTaskCountError || pendingCount === undefined || overSlaCount === undefined) {
      return NAV_GROUPS;
    }
    return NAV_GROUPS.map(group => {
      if (group.id !== 'pledge-operations-section') return group;
      return {
        ...group,
        items: group.items.map(item =>
          item.id === 'tasks'
            ? {
                ...item,
                badgeNode: <NavBadge pendingCount={pendingCount} overSlaCount={overSlaCount} />,
              }
            : item,
        ),
      };
    });
  }, [taskCountData?.pendingCount, taskCountData?.overSlaCount, isTaskCountError]);

  // -------------------------------------------------------------------------
  // Global Search
  // -------------------------------------------------------------------------

  // Pages and system screens only; see palette-search.ts (GH #4047).
  const handleSearch = useCallback(
    (query: string, category?: string): Promise<PaletteResult[]> =>
      Promise.resolve(searchPaletteEntries(query, category)),
    [],
  );

  const handleSearchSelect = useCallback((result: PaletteResult) => {
    const href = paletteEntryHref(result.id);
    if (href) {
      void navigate(href);
    }
  }, [navigate]);

  // -------------------------------------------------------------------------
  // TreeNav - active item derived from current path
  // -------------------------------------------------------------------------

  // Longest nav-path prefix, so a detail route (/tenants/<id>, /pledges/<id>)
  // keeps its parent item highlighted and its group open.
  const activeNavItemId = useMemo(
    () => resolveActiveNavItemId(location.pathname, NAV_ID_TO_PATH),
    [location.pathname],
  );

  const handleNavItemSelect = useCallback((id: string) => {
    const path = NAV_ID_TO_PATH[id];
    if (path) {
      navigate(path);
      setMobileOpen(false);
    }
  }, [navigate]);

  // -------------------------------------------------------------------------
  // Sidebar header and footer (shared between desktop and mobile)
  // -------------------------------------------------------------------------

  const sidebarHeader = useMemo(() => (
    <div className="h-[65px] flex items-center px-4 border-b border-recessed">
      <img
        src="https://cdn.aaim.app/logos/aaim-master-grey.svg"
        alt="Aaim"
        className="h-6"
      />
      <span className="ml-2 text-xs font-sans text-tertiary">
        Platform Admin
      </span>
    </div>
  ), []);

  const sidebarFooter = useMemo(() => (
    <div className="px-4 py-3 border-t border-recessed">
      <BuildVersion
        buildInfo={{ version: import.meta.env.VITE_BUILD_VERSION || '0.0.0-dev' }}
        format="short"
      />
    </div>
  ), []);

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  // Aaim-OPERATOR nav (platform.aaim.com:9303 -- AAIM_SUPER_ADMIN /
  // E2E_TEST_ADMIN). This node holds this portal's own nav groups, permissions
  // and roles, and is passed into PortalFrame's `sidebar` slot. The frame holds
  // no persona, tenant or permission knowledge; sharing the frame with
  // admin.aaim.com:9300 (FI-tenant) does NOT share the navigation.
  // See .claude/rules/scope-orientation-c4-first.md.
  const operatorNav = (
    <RoleBasedNav
      groups={enrichedNavGroups}
      activeItemId={activeNavItemId}
      onItemSelect={handleNavItemSelect}
      permissions={user?.permissions ?? []}
      roles={user?.roles ?? []}
      density="default"
      header={sidebarHeader}
      footer={sidebarFooter}
    />
  );

  return (
    <PortalFrame
      overlays={
        /* Command Palette (renders as modal overlay, activated by Cmd+K) */
        <CommandPalette
          categories={SEARCH_CATEGORIES}
          onSearch={handleSearch}
          onSelect={handleSearchSelect}
          placeholder="Search pages and settings..."
        />
      }
      sidebar={operatorNav}
      mobileOpen={mobileOpen}
      onMobileClose={() => setMobileOpen(false)}
      header={
        /* Top Navigation Bar */
        <AppHeader
          title={currentPageInfo.title}
          leading={
            /* Sidebar toggle: top-left, BEFORE the title (it opens the sidebar
               drawer, so it sits where the sidebar is). Mobile only. */
            <button
              onClick={() => setMobileOpen(true)}
              className="md:hidden p-2 -ml-2 text-secondary hover:text-primary"
              aria-label="Open menu"
            >
              <MenuIcon className="w-5 h-5" />
            </button>
          }
          actions={
            <>
              <CommandPaletteTrigger />
              <NotificationPanel
                notifications={notifications}
                onViewAll={() => navigate('/audit-logs')}
              />
              {/* Placement convention (GH #2305): last icon affordance before
                  the user pill, on every institutional SPA. */}
              <ThemeToggle />
              <UserProfileMenu
                user={userProfile}
                menuItems={userMenuItems}
                onSignOut={handleSignOut}
                showEmail
                showRole
              />
            </>
          }
          sticky
          backdrop
        />
      }
    >
      {/* DCF-62: sign out after 30 minutes without input (Robert's card of
          2026-11-02 19:16:47Z). */}
      <IdleSignOut onSignOut={handleSignOut} channelName={IDLE_SIGN_OUT_CHANNEL} />
      {/* Page Content */}
      <Outlet />
    </PortalFrame>
  );
}
